import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { FileText } from "@/lib/icons";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useSearchParamText } from "@/hooks/use-search-param-text";
import { orpc } from "@/lib/orpc";
import { optionalOneOf, KEEP_PLACE, searchPatch, optionalSearch } from "@/lib/search";
import { useCan } from "@/lib/policy";
import { CreateChargeDialog } from "@/routes/_authenticated/_financials/charges/-components/create-charge-dialog";
import { ChargesCard } from "./-components/financials/charges-card";
import { MoveOutCloseoutCard } from "./-components/financials/closeout-card";
import { InvoicesCard } from "./-components/financials/invoices-card";
import { PaymentMethodsCard } from "./-components/financials/payment-methods-card";
import { StatementDialog } from "./-components/financials/statement-dialog";
import { TransactionsCard } from "./-components/financials/transactions-card";
import { ReportedPaymentsCard } from "./-components/billing/reported-payments-card";

type Section = "charges" | "invoices" | "transactions" | "methods";
type StatementSearch = { section?: Exclude<Section, "invoices">; q?: string };

export const Route = createFileRoute("/_authenticated/_care/residents/$residentId/statement")({
  validateSearch: (search: Record<string, unknown>): StatementSearch => ({
    section: optionalOneOf(["charges", "transactions", "methods"] as const, search.section),
    q: optionalSearch(search.q),
  }),
  component: FinancialsTab,
});

const SECTIONS: Array<[Section, string]> = [
  ["charges", "Charge series"],
  ["invoices", "Invoices"],
  ["transactions", "Transactions"],
  ["methods", "Payment methods"],
];

/** Charge, invoice, payment: one section each, under the account balance. */
function FinancialsTab() {
  const { residentId } = Route.useParams();
  const { data: resident } = useSuspenseQuery(
    orpc.care.residents.get.queryOptions({ input: { residentId } }),
  );
  const canManage = useCan("financial:manage", resident.facilityId);
  const canReconcile = useCan("financial:reconcile", resident.facilityId);
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = searchPatch(navigate);
  const section: Section = params.section ?? "invoices";
  const setSection = (next: Section) =>
    void navigate({
      search: { section: next === "invoices" ? undefined : next },
      ...KEEP_PLACE,
    });
  const { text: search, change: setSearch } = useSearchParamText(params.q, (q) => patch({ q }));
  const [statementOpen, setStatementOpen] = useState(false);
  const [creatingCharge, setCreatingCharge] = useState(false);
  const closeout = useQuery(
    orpc.financials.statements.closeout.queryOptions({ input: { residentId } }),
  );

  return (
    <div className="flex flex-col gap-4">
      <Card className="py-0">
        <CardContent className="flex flex-wrap items-center justify-between gap-4 px-6 py-4">
          <p className="text-muted-foreground text-sm">Current balance</p>
          <div className="flex items-center gap-4">
            {/* Never "$0.00" for a balance that has not been read. */}
            {closeout.isPending ? (
              <Skeleton className="h-7 w-24" />
            ) : closeout.isError ? (
              <p className="text-muted-foreground text-sm">
                Could not load the balance.{" "}
                <Button
                  variant="link"
                  className="h-auto p-0"
                  onClick={() => void closeout.refetch()}
                >
                  Try again
                </Button>
              </p>
            ) : (
              <p className="text-2xl leading-none font-semibold tabular-nums">
                {closeout.data.openBalance}
              </p>
            )}
            <Button variant="outline" onClick={() => setStatementOpen(true)}>
              <FileText className="size-4" />
              Statement
            </Button>
          </div>
        </CardContent>
      </Card>

      {statementOpen ? (
        <StatementDialog residentId={residentId} onClose={() => setStatementOpen(false)} />
      ) : null}
      {canManage ? <MoveOutCloseoutCard residentId={residentId} canManage={canManage} /> : null}

      <div>
        <ToggleGroup
          type="single"
          variant="outline"
          value={section}
          onValueChange={(value) => (value ? setSection(value as Section) : null)}
          aria-label="Financial sections"
        >
          {SECTIONS.map(([value, label]) => (
            <ToggleGroupItem key={value} value={value} className="px-3">
              {label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <div className="mt-4 flex flex-col gap-4">
          {section === "charges" ? (
            <ChargesCard
              residentId={residentId}
              canManage={canManage}
              onCreate={() => setCreatingCharge(true)}
            />
          ) : null}
          {section === "invoices" ? (
            <InvoicesCard
              residentId={residentId}
              canManage={canManage}
              onCreateCharge={() => setCreatingCharge(true)}
              search={search}
              onSearch={setSearch}
            />
          ) : null}
          {section === "transactions" ? (
            <TransactionsCard
              residentId={residentId}
              canReconcile={canReconcile}
              search={search}
              onSearch={setSearch}
            />
          ) : null}
          {section === "methods" ? (
            <>
              <ReportedPaymentsCard residentId={residentId} />
              <PaymentMethodsCard
                residentId={residentId}
                canManage={canManage}
                canReconcile={canReconcile}
              />
            </>
          ) : null}
        </div>
      </div>

      <CreateChargeDialog
        open={creatingCharge}
        onOpenChange={setCreatingCharge}
        presetResidentId={residentId}
      />
    </div>
  );
}
